import { useState } from 'react';

import { Container, Draggable, DropResult } from 'react-smooth-dnd';

import { Card, Divider } from '@cfa/react-components';

import { cn } from '@/utils/tailwind';

import { BaseReferenceDTO } from '@/services/content-api-types';
import { TaskProps } from '@/types/types';

import Constants from 'constants/index';

import { AddTask } from './AddTask';
import PlanStepContainer, { SectionFunctionProps } from './PlanStepContainer';
import SectionButtons from './SectionButtons';
import Title from './Title';

import { Plan, PlanSection } from '@/_generated/api-clients/xp';

interface SectionProps {
  plan?: Plan;
  refetch?: () => void;
  section: PlanSection;
  taskUpdated?: boolean;
  isAllowedToEdit?: boolean;
  editSectionTitle?: (section: PlanSection) => boolean;
  renameSectionPopUp?: (sectionId: string) => void;
  deleteSectionPopUp?: (sectionId: string, sectionName: string) => void;
  onClickThreeDotMenuDeleteTask?: ({
    id,
    name,
    sectionId,
    type,
  }: SectionFunctionProps) => void;
  handleResourceOrQuizPreview?: (document: BaseReferenceDTO | string) => void;
  addProcedure?: (id?: string) => void;
  addQuiz?: () => void;
  onCancel?: () => void;
  onEdit?: (
    type: string,
    title: string | null,
    task: TaskProps,
    id: string,
  ) => void;
  onDrop?: (
    e: {
      addedIndex: number;
      payload: { id: string; sectionId: string };
      removedIndex: number;
    },
    id: string,
  ) => void;
  setActiveSectionId?: (id: string) => void;
  setIndexToAdd?: (index: DropResult) => void;
  isPreview?: boolean;
  language?: string;
  index: number;
  editProcedureResource?: ({
    stepId,
    sectionId,
  }: {
    stepId: string;
    sectionId?: string;
  }) => void;
  isReadOnly?: boolean;
  className?: string;
}

const Section: React.FC<SectionProps> = ({
  isPreview,
  plan,
  section,
  isAllowedToEdit,
  editSectionTitle,
  renameSectionPopUp,
  deleteSectionPopUp,
  onClickThreeDotMenuDeleteTask,
  handleResourceOrQuizPreview,
  addProcedure,
  addQuiz,
  onCancel,
  onEdit,
  onDrop,
  setActiveSectionId,
  setIndexToAdd,
  taskUpdated,
  language,
  index,
  editProcedureResource,
  isReadOnly = false,
  className,
}) => {
  const [showAddTask, setShowAddTask] = useState(false);
  const sectionId = section.id;
  const isEditable = isAllowedToEdit ?? false;
  const isTaskUpdated = taskUpdated ?? false;

  return (
    <Card
      data-testid={`SectionCard-${index}`}
      elevation={1}
      className={cn('relative mx-0 p-4', className)}
    >
      <Title
        deleteSectionPopUp={
          deleteSectionPopUp as (sectionId: string, sectionName: string) => void
        }
        editSectionTitle={editSectionTitle as (title: PlanSection) => boolean}
        index={index}
        isAllowedToEdit={isEditable}
        isPreview={isPreview}
        onCancel={onCancel as () => void}
        onEdit={updatedSectionTitle =>
          onEdit?.(
            Constants.TRAINING_MENU_OPTIONS.SECTION,
            updatedSectionTitle,
            { id: '', name: '', note: '', url: '' },
            sectionId,
          )
        }
        renameSectionPopUp={renameSectionPopUp}
        section={section}
        isReadOnly={isReadOnly}
      />

      <Divider
        className={cn('mx-0! mb-4!', isAllowedToEdit ? 'mt-2!' : 'mt-4!')}
        variant="fullLength"
      />

      <div className="[&_.show-on-drop]:bg-outline-3 pathway-smooth-dnd-container w-full [&_.show-on-drop]:rounded-[5px] [&_.show-on-drop]:p-[10px]">
        {/** @ts-expect-error it's yelling about children but we need children for the drag and drop */}
        <Container
          dragHandleSelector=".step-in-section-drag-handle"
          dropPlaceholder={{
            className: 'shadow-on-drop',
          }}
          getChildPayload={idx => ({
            ...plan?.content?.sections?.find(sect => sect.id === section.id)
              ?.steps?.[idx],
            sectionId: section.id,
          })}
          groupName="steps"
          nonDragAreaSelector="pathway-disabled"
          onDragEnter={() =>
            (setActiveSectionId as (id: string) => void)(section.id)
          }
          onDrop={e =>
            (onDrop as (dropResult: DropResult, id: string) => void)(
              e,
              sectionId,
            )
          }
          onDropReady={addedIndex =>
            (setIndexToAdd as (dropResult: DropResult) => void)(addedIndex)
          }
        >
          {section.steps?.map(step => (
            // @ts-expect-error it's yelling about children but we need children for the drag and drop
            <Draggable
              key={step.id}
              className={cn(isReadOnly && 'pathway-disabled')}
            >
              <PlanStepContainer
                className={cn(
                  isPreview || isReadOnly
                    ? 'no-drag pathway-disabled'
                    : 'step-in-section-drag-handle',
                )}
                handleResourceOrQuizPreview={handleResourceOrQuizPreview}
                isAllowedToEdit={isEditable}
                isPreview={isPreview}
                language={language}
                onCancel={onCancel as () => void}
                onDelete={onClickThreeDotMenuDeleteTask as () => void}
                onEdit={onEdit}
                editProcedureResource={editProcedureResource}
                sectionId={sectionId}
                step={step}
                taskUpdated={isTaskUpdated}
                isReadOnly={isReadOnly}
              />
            </Draggable>
          ))}
        </Container>
      </div>

      {showAddTask && (
        <AddTask
          isInSection
          plan={plan}
          sectionId={sectionId}
          setShowAddTask={setShowAddTask}
        />
      )}
      {!isPreview && !isReadOnly && (
        <SectionButtons
          addProcedure={addProcedure}
          addQuiz={addQuiz as () => void}
          setShowAddTask={setShowAddTask}
          showAddTask={showAddTask}
        />
      )}
    </Card>
  );
};

export default Section;
